/**
 * Federal tax constants, versioned by tax year (spec §9).
 *
 * A new tax year is a DATA edit here, not a code change. Every value below is
 * dated and cited. VERIFY against current law before relying on a projection.
 *
 * ── Tax year 2026 sources ────────────────────────────────────────────────
 * IRS Rev. Proc. 2025-32 (released 2025-10-09), incorporating the 2025 tax act
 * ("One Big Beautiful Bill Act", OBBBA, enacted 2025-07-04).
 *   - Brackets / std deduction / LTCG / additional-65 deduction:
 *     https://www.irs.gov/pub/irs-drop/rp-25-32.pdf
 *     https://taxfoundation.org/data/all/federal/2026-tax-brackets/
 *   - Senior bonus deduction (OBBBA §70103): Kiplinger / Schwab / PGPF (2025).
 *   - NIIT (fixed since 2013): CRS IF11820.
 *   - SS taxability thresholds (fixed 1983/1993): SSA.
 *   - FICA / SS wage base $184,500: SSA 2026 COLA fact sheet; PayrollOrg.
 *   - SALT cap $40,400, phase-down at $505,000: OBBBA; Kiplinger.
 */
import type { FilingStatus } from '../../model/types';

/** The date the constants in this file were last verified against current law. */
export const CONSTANTS_AS_OF = '2026-08-03';

export interface Bracket {
  /** Marginal rate as a decimal, e.g. 0.22. */
  rate: number;
  /** Taxable income at which this rate STARTS. First bracket starts at 0. */
  floor: number;
}

export interface FederalTaxYear {
  year: number;
  ordinaryBrackets: Record<FilingStatus, Bracket[]>;
  standardDeduction: Record<FilingStatus, number>;
  /** Additional standard deduction per qualifying 65+/blind condition. */
  additional65Deduction: Record<FilingStatus, number>;
  /** OBBBA temporary senior bonus deduction (tax years 2025-2028). */
  seniorBonusDeduction: {
    perPerson: number;
    phaseoutStart: Record<FilingStatus, number>;
    phaseoutRate: number; // per person, of MAGI over the threshold
    expiresAfterYear: number;
  };
  /** Long-term capital gains / qualified dividend breakpoints (taxable income). */
  ltcgBreakpoints: Record<FilingStatus, { rate0Ceiling: number; rate15Ceiling: number }>;
  niit: {
    rate: number;
    magiThreshold: Record<FilingStatus, number>; // NOT inflation-indexed
  };
  socialSecurityTaxability: {
    // Provisional (combined) income tiers. NOT inflation-indexed.
    tier1: Record<FilingStatus, number>; // above this, up to 50% taxable
    tier2: Record<FilingStatus, number>; // above this, up to 85% taxable
  };
  fica: {
    ssRateEmployee: number;
    ssRateSelfEmployment: number;
    ssWageBase: number;
    medicareRateEmployee: number;
    medicareRateSelfEmployment: number;
    additionalMedicareRate: number;
    additionalMedicareThreshold: Record<FilingStatus, number>; // NOT indexed
  };
  saltCap: {
    cap: Record<FilingStatus, number>;
    phasedownMagiStart: Record<FilingStatus, number>;
    phasedownRate: number;
    floor: Record<FilingStatus, number>;
  };
}

// QW (qualifying widow/surviving spouse) uses the MFJ schedule throughout.
const FY2026: FederalTaxYear = {
  year: 2026,
  ordinaryBrackets: {
    single: [
      { rate: 0.1, floor: 0 },
      { rate: 0.12, floor: 12_400 },
      { rate: 0.22, floor: 50_400 },
      { rate: 0.24, floor: 105_700 },
      { rate: 0.32, floor: 201_775 },
      { rate: 0.35, floor: 256_225 },
      { rate: 0.37, floor: 640_600 },
    ],
    mfj: [
      { rate: 0.1, floor: 0 },
      { rate: 0.12, floor: 24_800 },
      { rate: 0.22, floor: 100_800 },
      { rate: 0.24, floor: 211_400 },
      { rate: 0.32, floor: 403_550 },
      { rate: 0.35, floor: 512_450 },
      { rate: 0.37, floor: 768_700 },
    ],
    hoh: [
      { rate: 0.1, floor: 0 },
      { rate: 0.12, floor: 17_700 },
      { rate: 0.22, floor: 67_450 },
      { rate: 0.24, floor: 105_700 },
      { rate: 0.32, floor: 201_775 },
      { rate: 0.35, floor: 256_200 },
      { rate: 0.37, floor: 640_600 },
    ],
    qw: [
      { rate: 0.1, floor: 0 },
      { rate: 0.12, floor: 24_800 },
      { rate: 0.22, floor: 100_800 },
      { rate: 0.24, floor: 211_400 },
      { rate: 0.32, floor: 403_550 },
      { rate: 0.35, floor: 512_450 },
      { rate: 0.37, floor: 768_700 },
    ],
  },
  standardDeduction: { single: 16_100, mfj: 32_200, hoh: 24_150, qw: 32_200 },
  additional65Deduction: { single: 2_050, mfj: 1_650, hoh: 2_050, qw: 1_650 },
  seniorBonusDeduction: {
    perPerson: 6_000,
    phaseoutStart: { single: 75_000, mfj: 150_000, hoh: 75_000, qw: 150_000 },
    phaseoutRate: 0.06,
    expiresAfterYear: 2028,
  },
  ltcgBreakpoints: {
    single: { rate0Ceiling: 49_450, rate15Ceiling: 545_500 },
    mfj: { rate0Ceiling: 98_900, rate15Ceiling: 613_700 },
    hoh: { rate0Ceiling: 66_200, rate15Ceiling: 579_600 },
    qw: { rate0Ceiling: 98_900, rate15Ceiling: 613_700 },
  },
  niit: {
    rate: 0.038,
    magiThreshold: { single: 200_000, mfj: 250_000, hoh: 200_000, qw: 250_000 },
  },
  socialSecurityTaxability: {
    tier1: { single: 25_000, mfj: 32_000, hoh: 25_000, qw: 32_000 },
    tier2: { single: 34_000, mfj: 44_000, hoh: 34_000, qw: 44_000 },
  },
  fica: {
    ssRateEmployee: 0.062,
    ssRateSelfEmployment: 0.124,
    ssWageBase: 184_500,
    medicareRateEmployee: 0.0145,
    medicareRateSelfEmployment: 0.029,
    additionalMedicareRate: 0.009,
    additionalMedicareThreshold: { single: 200_000, mfj: 250_000, hoh: 200_000, qw: 250_000 },
  },
  saltCap: {
    cap: { single: 40_400, mfj: 40_400, hoh: 40_400, qw: 40_400 },
    phasedownMagiStart: { single: 505_000, mfj: 505_000, hoh: 505_000, qw: 505_000 },
    phasedownRate: 0.3,
    floor: { single: 10_000, mfj: 10_000, hoh: 10_000, qw: 10_000 },
  },
};

export const TAX_YEARS: Record<number, FederalTaxYear> = {
  2026: FY2026,
};

/** OBBBA SALT schedule: the cap grows 1%/yr (statutory, not CPI) through this
 *  year, then reverts to the unindexed $10,000 permanently. */
const SALT_ESCALATOR_LAST_YEAR = 2029;

const indexedCache = new Map<string, FederalTaxYear>();

/**
 * Returns the federal constants for `year`. For years past the latest defined
 * tax year, pass the plan's general inflation rate to project the law forward
 * the way the IRS actually administers it:
 *
 * Indexed annually under IRC §1(f) (chained CPI) — approximated here by the
 * plan's inflation assumption: bracket floors, standard deduction, the 65+
 * additional deduction, LTCG breakpoints, and the SS wage base (wage-indexed
 * in law; general inflation is the proxy available).
 *
 * Fixed in statute, deliberately NOT indexed (real-dollar creep is a feature
 * of current law, and the projection should show it): the NIIT MAGI threshold,
 * the Additional Medicare threshold, both SS-taxability tiers, and the senior
 * bonus deduction (expires 2028 anyway).
 *
 * SALT follows its own statutory schedule regardless of inflation: +1%/yr
 * through 2029, then a permanent reversion to an unindexed $10,000 cap.
 */
export function federalTaxYear(year: number, inflationRate?: number): FederalTaxYear {
  const exact = TAX_YEARS[year];
  if (exact) return exact;
  const years = Object.keys(TAX_YEARS).map(Number).sort((a, b) => a - b);
  const latest = years[years.length - 1]!;
  const base = TAX_YEARS[latest]!;
  if (year <= base.year) return base;

  const rate = inflationRate ?? 0;
  const key = `${year}|${rate}`;
  const cached = indexedCache.get(key);
  if (cached) return cached;

  const n = year - base.year;
  const k = Math.pow(1 + rate, n);
  const idx = (v: number) => Math.round(v * k);
  const idxByStatus = (rec: Record<FilingStatus, number>): Record<FilingStatus, number> => ({
    single: idx(rec.single), mfj: idx(rec.mfj), hoh: idx(rec.hoh), qw: idx(rec.qw),
  });
  const idxBrackets = (brs: Bracket[]): Bracket[] =>
    brs.map((b) => ({ rate: b.rate, floor: idx(b.floor) }));

  const saltEscalator = Math.pow(1.01, Math.min(year, SALT_ESCALATOR_LAST_YEAR) - base.year);
  const saltReverted = year > SALT_ESCALATOR_LAST_YEAR;
  const saltCap: FederalTaxYear['saltCap'] = saltReverted
    ? {
        cap: { single: 10_000, mfj: 10_000, hoh: 10_000, qw: 10_000 },
        phasedownMagiStart: { single: Infinity, mfj: Infinity, hoh: Infinity, qw: Infinity },
        phasedownRate: 0,
        floor: { single: 10_000, mfj: 10_000, hoh: 10_000, qw: 10_000 },
      }
    : {
        cap: {
          single: Math.round(base.saltCap.cap.single * saltEscalator),
          mfj: Math.round(base.saltCap.cap.mfj * saltEscalator),
          hoh: Math.round(base.saltCap.cap.hoh * saltEscalator),
          qw: Math.round(base.saltCap.cap.qw * saltEscalator),
        },
        phasedownMagiStart: {
          single: Math.round(base.saltCap.phasedownMagiStart.single * saltEscalator),
          mfj: Math.round(base.saltCap.phasedownMagiStart.mfj * saltEscalator),
          hoh: Math.round(base.saltCap.phasedownMagiStart.hoh * saltEscalator),
          qw: Math.round(base.saltCap.phasedownMagiStart.qw * saltEscalator),
        },
        phasedownRate: base.saltCap.phasedownRate,
        floor: base.saltCap.floor,
      };

  const out: FederalTaxYear = {
    year,
    ordinaryBrackets: {
      single: idxBrackets(base.ordinaryBrackets.single),
      mfj: idxBrackets(base.ordinaryBrackets.mfj),
      hoh: idxBrackets(base.ordinaryBrackets.hoh),
      qw: idxBrackets(base.ordinaryBrackets.qw),
    },
    standardDeduction: idxByStatus(base.standardDeduction),
    additional65Deduction: idxByStatus(base.additional65Deduction),
    seniorBonusDeduction: base.seniorBonusDeduction,
    ltcgBreakpoints: {
      single: { rate0Ceiling: idx(base.ltcgBreakpoints.single.rate0Ceiling), rate15Ceiling: idx(base.ltcgBreakpoints.single.rate15Ceiling) },
      mfj: { rate0Ceiling: idx(base.ltcgBreakpoints.mfj.rate0Ceiling), rate15Ceiling: idx(base.ltcgBreakpoints.mfj.rate15Ceiling) },
      hoh: { rate0Ceiling: idx(base.ltcgBreakpoints.hoh.rate0Ceiling), rate15Ceiling: idx(base.ltcgBreakpoints.hoh.rate15Ceiling) },
      qw: { rate0Ceiling: idx(base.ltcgBreakpoints.qw.rate0Ceiling), rate15Ceiling: idx(base.ltcgBreakpoints.qw.rate15Ceiling) },
    },
    niit: base.niit,
    socialSecurityTaxability: base.socialSecurityTaxability,
    fica: { ...base.fica, ssWageBase: idx(base.fica.ssWageBase) },
    saltCap,
  };
  indexedCache.set(key, out);
  return out;
}
